import React, {
    useCallback,
    useEffect,
    useState,
} from "react";

import {
    useNavigate,
} from "react-router-dom";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import type {
    RootState,
    AppDispatch,
} from "../../store";

import {
    setJourney,
} from "../../store/journeySlice";

import MobileFrame from "../../components/MobileFrame";

import Lottie from "lottie-react";

import {
    updateJourneyPhase,
    rejectPreApproval
} from "../../api/journey.api";

import {
    ChevronDown,
    ChevronUp,
    Loader2,
    AlertCircle,
    XCircle,
} from "lucide-react";

import loaderAnimation
    from "../../assets/Cleverpe_Loading.json";

import successAnimation
    from "../../assets/sucess.json";

import rejectedAnimation
    from "../../assets/failure.json";

import {
    fetchGatewayPreApproval,
} from "../../api/gateway.api";

import {
    setSchemes,
} from "../../store/schemeSlice";

import {
    fetchApplicableSchemes,
} from "../../api/scheme.api";


/*
 * =========================================================
 * TYPES
 * =========================================================
 */

type PreApprovalStatus =
    | "NOT_RECEIVED"
    | "APPROVED"
    | "REJECTED"
    | "PENDING";

type PageState =
    | "loading"
    | "approved"
    | "rejected"
    | "pending"
    | "error";


/*
 * =========================================================
 * CONSTANTS
 * =========================================================
 */

const POLL_INTERVAL = 3000;

const MAX_POLL_ERRORS = 5;


/*
 * =========================================================
 * COMPONENT
 * =========================================================
 */

const PreApproval: React.FC = () => {

    const dispatch =
        useDispatch<AppDispatch>();

    const navigate =
        useNavigate();


    /*
     * =========================================================
     * REDUX
     * =========================================================
     */

    const journey =
        useSelector(
            (state: RootState) =>
                state.journey.journey
        );
    const schemes =
        useSelector(
            (state: RootState) =>
                state.scheme.schemes
        );


    /*
     * =========================================================
     * STATE
     * =========================================================
     */

    const [pageState, setPageState] =
        useState<PageState>("loading");

    const [approvedAmount, setApprovedAmount] =
        useState<number | null>(null);

    const [errorMessage, setErrorMessage] =
        useState<string>("");

    const [isContinuing, setIsContinuing] =
        useState(false);

    const [showCaprionSchemes, setShowCaprionSchemes] =
        useState(false);


    /*
     * =========================================================
     * FORMAT AMOUNT
     * =========================================================
     */

    const formatAmount =
        (amount: number): string => {

            return new Intl.NumberFormat(
                "en-IN",
                {
                    style: "currency",
                    currency: "INR",
                    maximumFractionDigits: 0,
                }
            ).format(amount);
        };


    /*
     * =========================================================
     * EXTRACT APPROVED AMOUNT
     * =========================================================
     */

    const getApprovedAmount =
        (data: any): number | null => {

            const rawAmount =
                data?.approvedAmount ??
                data?.approved_amount ??
                data?.approvalLimit ??
                data?.preApprovedAmount;

            if (
                rawAmount === null ||
                rawAmount === undefined ||
                rawAmount === ""
            ) {
                return null;
            }

            const amount =
                Number(rawAmount);

            return Number.isFinite(amount) &&
                amount > 0
                ? amount
                : null;
        };


    /*
     * =========================================================
     * FETCH PRE-APPROVAL STATUS
     * =========================================================
     */

    const checkPreApproval =
        useCallback(
            async (): Promise<
                PreApprovalStatus | null
            > => {

                if (!journey) {

                    setPageState("error");

                    setErrorMessage(
                        "Journey session not found. Please reopen your application."
                    );

                    return null;
                }


                try {

                    const response =
                        await fetchGatewayPreApproval({

                            lender:
                                journey.lender,

                            flowType:
                                journey.flowType,
                        });


                    const data =
                        response?.data;


                    const status =
                        String(
                            data?.status ?? ""
                        )
                            .trim()
                            .toUpperCase();


                    /*
                     * =================================================
                     * NOT RECEIVED / IN PROGRESS
                     * =================================================
                     */

                    if (
                        status === "" ||
                        status === "NOT_RECEIVED" ||
                        status === "IN_PROGRESS"
                    ) {

                        setPageState(
                            "loading"
                        );

                        return "NOT_RECEIVED";
                    }


                    /*
                     * =================================================
                     * APPROVED
                     * =================================================
                     */

                    if (status === "APPROVED") {

                        const amount =
                            getApprovedAmount(
                                data
                            );

                        if (amount === null) {
                            setPageState("error");

                            setErrorMessage(
                                "Approved amount was not received."
                            );

                            return "APPROVED";
                        }

                        setApprovedAmount(
                            amount
                        );

                        try {

                            const schemeResponse =
                                await fetchApplicableSchemes({
                                    lender: journey.lender,
                                    amount,
                                });

                            const schemes =
                                schemeResponse.data.availableSchemes;

                            if (
                                !Array.isArray(schemes) ||
                                schemes.length === 0
                            ) {
                                throw new Error(
                                    "No applicable schemes found."
                                );
                            }

                            dispatch(
                                setSchemes({
                                    amount: schemeResponse.data.amount,
                                    schemes,
                                })
                            );

                        } catch (error) {

                            console.error(
                                "Failed to fetch applicable schemes:",
                                error
                            );

                            setPageState("error");

                            setErrorMessage(
                                error instanceof Error
                                    ? error.message
                                    : "Unable to fetch applicable schemes."
                            );

                            return "APPROVED";
                        }

                        setPageState(
                            "approved"
                        );

                        return "APPROVED";
                    }


                    /*
                     * =================================================
                     * REJECTED
                     * =================================================
                     */

                    if (
                        status === "REJECTED"
                    ) {

                        setPageState(
                            "rejected"
                        );

                        return "REJECTED";
                    }


                    /*
                     * =================================================
                     * PENDING
                     * =================================================
                     */

                    if (status === "PENDING") {

                        const amount =
                            getApprovedAmount(data);

                        setApprovedAmount(amount);

                        if (amount !== null) {

                            try {

                                const schemeResponse =
                                    await fetchApplicableSchemes({
                                        lender: journey.lender,
                                        amount,
                                    });

                                const schemes =
                                    schemeResponse.data.availableSchemes;

                                if (Array.isArray(schemes) && schemes.length > 0) {

                                    dispatch(
                                        setSchemes({
                                            amount: schemeResponse.data.amount,
                                            schemes,
                                        })
                                    );
                                }

                            } catch (error) {

                                console.error(
                                    "Failed to fetch applicable schemes for pending status:",
                                    error
                                );

                                // Don't hard-fail the pending screen if schemes
                                // can't be fetched — just show it without the
                                // scheme breakdown.
                            }
                        }

                        setPageState("pending");

                        return "PENDING";
                    }


                    /*
                     * =================================================
                     * UNKNOWN STATUS
                     * =================================================
                     *
                     * Do not treat an unknown provider status
                     * as rejection.
                     *
                     * Keep polling.
                     */

                    console.warn(
                        "Unknown pre-approval status:",
                        status
                    );

                    setPageState(
                        "loading"
                    );

                    return "NOT_RECEIVED";

                } catch (error) {

                    console.error(
                        "Pre-approval status check failed:",
                        error
                    );

                    /*
                     * Do not immediately fail the UI because
                     * this API is being polled.
                     *
                     * The caller decides whether repeated
                     * failures should eventually become an error.
                     */

                    return null;
                }

            },
            [journey]
        );


    /*
     * =========================================================
     * INITIAL FETCH + SAFE POLLING
     * =========================================================
     */

    useEffect(() => {

        if (!journey) {

            setPageState("error");

            setErrorMessage(
                "Journey session not found. Please reopen your application."
            );

            return;
        }


        let isActive = true;

        let timeoutId:
            ReturnType<typeof setTimeout> | null =
            null;

        let consecutiveErrors = 0;


        const poll =
            async (): Promise<void> => {

                if (!isActive) {
                    return;
                }


                const status =
                    await checkPreApproval();


                if (!isActive) {
                    return;
                }


                /*
                 * =================================================
                 * TERMINAL STATES
                 * =================================================
                 */

                if (
                    status === "APPROVED" ||
                    status === "REJECTED" ||
                    status === "PENDING"
                ) {

                    return;
                }


                /*
                 * =================================================
                 * NETWORK/API ERROR
                 * =================================================
                 */

                if (status === null) {

                    consecutiveErrors += 1;

                    if (
                        consecutiveErrors >=
                        MAX_POLL_ERRORS
                    ) {

                        setPageState(
                            "error"
                        );

                        setErrorMessage(
                            "We were unable to check your pre-approval status. Please try again."
                        );

                        return;
                    }

                } else {

                    /*
                     * Successful API response.
                     * Reset temporary error count.
                     */

                    consecutiveErrors = 0;
                }


                /*
                 * =================================================
                 * SCHEDULE NEXT POLL
                 * =================================================
                 *
                 * setTimeout is intentionally used instead
                 * of setInterval so another request cannot
                 * start before the previous one finishes.
                 */

                timeoutId =
                    setTimeout(
                        poll,
                        POLL_INTERVAL
                    );
            };


        poll();


        /*
         * =================================================
         * CLEANUP
         * =================================================
         */

        return () => {

            isActive = false;

            if (timeoutId) {

                clearTimeout(
                    timeoutId
                );

                timeoutId = null;
            }
        };

    }, [
        journey,
        checkPreApproval,
    ]);


    /*
     * =========================================================
     * CONTINUE
     * =========================================================
     */

    const handleContinue =
        async (): Promise<void> => {

            if (isContinuing) {
                return;
            }

            if (!journey) {

                setErrorMessage(
                    "Journey session not found. Please reopen your application."
                );

                setPageState("error");

                return;
            }

            /*
             * Only PENDING needs orderAmount corrected —
             * BRE approved a lower amount than requested.
             * APPROVED already matches/exceeds the request,
             * so orderAmount stays untouched there.
             */

            const isPendingFlow =
                pageState === "pending";

            if (isPendingFlow && approvedAmount === null) {

                setErrorMessage(
                    "Approved amount is missing. Please try again."
                );

                return;
            }

            try {

                setIsContinuing(true);

                setErrorMessage("");

                const response =
                    await updateJourneyPhase(
                        "SCHEME_SELECTION",
                        isPendingFlow
                            ? approvedAmount!
                            : undefined
                    );

                if (!response?.data) {

                    throw new Error(
                        "Invalid response received while updating journey."
                    );
                }

                dispatch(
                    setJourney(
                        response.data
                    )
                );

                navigate(
                    "/nscheme-selection",
                    { replace: true }
                );

            } catch (error) {

                console.error(
                    "Failed to update journey phase:",
                    error
                );

                setIsContinuing(false);

                setErrorMessage(
                    error instanceof Error
                        ? error.message
                        : "Unable to continue. Please try again."
                );
            }
        };


    /*
     * =========================================================
     * BACK / LENDER CALLBACK
     * =========================================================
     */

    const handleBack = async (): Promise<void> => {
        try {
            await rejectPreApproval();

            window.location.href =
                journey?.callbackUrl ||
                "https://cleverpe.com";
        } catch (error) {
            console.error(
                "Failed to reject pre-approval:",
                error
            );
        }
    };


    /*
     * =========================================================
     * LOADING SCREEN
     * =========================================================
     */

    const renderLoading =
        () => (

            <div
                className="
                    relative
                    flex
                    flex-col
                    items-center
                    justify-center
                    h-full
                    px-8
                    text-center
                "
            >

                <div
                    className="w-44 h-44"
                    aria-hidden="true"
                >

                    <Lottie
                        animationData={
                            loaderAnimation
                        }
                        loop
                        autoplay
                    />

                </div>


                <h2
                    className="
                        mt-3
                        text-base
                        font-semibold
                        text-gray-900
                    "
                >
                    Fetching your pre-approval
                </h2>


                <p
                    className="
                        mt-2
                        max-w-xs
                        text-[12px]
                        leading-relaxed
                        text-gray-500
                    "
                >
                    This might take a moment while we
                    check your application details.
                </p>


                <p
                    className="
                        absolute
                        bottom-5
                        text-center
                        text-[11px]
                        font-semibold
                        tracking-wide
                        text-gray-400
                    "
                >
                    CleverPe
                </p>

            </div>
        );


    /*
     * =========================================================
     * APPROVED SCREEN
     * =========================================================
     */

    const renderApproved =
        () => (

            <div
                className="
                    relative
                    flex
                    flex-col
                    h-full
                    px-6
                    pt-6
                    pb-5
                "
            >

                {/* Header */}

                <div
                    className="
                        flex
                        items-center
                        justify-between
                    "
                >

                    <div />

                    <img
                        src="/CleverPeLogos/CleverPE_Black_icon.svg"
                        alt="CleverPe"
                        className="
                            h-6
                            w-6
                            opacity-90
                        "
                    />

                </div>


                {/* Main content */}

                <div
                    className="
                        flex
                        flex-col
                        items-center
                        flex-1
                        text-center
                        pt-3
                    "
                >

                    <div
                        className="-mt-9 w-40 h-40"
                        aria-hidden="true"
                    >

                        <Lottie
                            animationData={
                                successAnimation
                            }
                            loop={false}
                            autoplay
                        />

                    </div>


                    <h2
                        className="
                            -mt-3
                            text-xl
                            font-bold
                            text-gray-900
                        "
                    >
                        Congratulations!
                    </h2>


                    <p
                        className="
                            mt-1.5
                            text-sm
                            leading-relaxed
                            text-gray-500
                        "
                    >
                        You are approved for a loan amount up to
                    </p>


                    {approvedAmount !== null && (

                        <p
                            className="
                                mt-4
                                text-4xl
                                font-bold
                                bg-gradient-to-r
                                from-indigo-500
                                via-purple-500
                                to-blue-500
                                text-transparent
                                bg-clip-text
                            "
                        >
                            {formatAmount(
                                approvedAmount
                            )}
                        </p>

                    )}


                    {/* Available lenders */}

                    <div
                        className="
                            w-full
                            mt-7
                            text-left
                        "
                    >

                        <p
                            className="
                                mb-2
                                text-[12px]
                                font-semibold
                                text-gray-700
                            "
                        >
                            Available lenders
                        </p>


                        <div
                            className="
                                w-full
                                overflow-hidden
                                rounded-xl
                                border
                                border-green-400
                                bg-white
                                py-1
                            "
                        >

                            <div
                                className="
                                    flex
                                    items-center
                                    justify-between
                                    px-4
                                    py-3
                                "
                            >

                                <p
                                    className="
                                        text-[13px]
                                        font-bold
                                        text-gray-900
                                    "
                                >
                                    Caprion:

                                    <span
                                        className="
                                            ml-1
                                            text-gray-900
                                        "
                                    >
                                        ₹
                                        {approvedAmount !== null
                                            ? approvedAmount.toLocaleString(
                                                "en-IN"
                                            )
                                            : "--"}
                                    </span>
                                </p>


                                <button
                                    type="button"
                                    onClick={() =>
                                        setShowCaprionSchemes(
                                            previous =>
                                                !previous
                                        )
                                    }
                                    aria-expanded={
                                        showCaprionSchemes
                                    }
                                    className="
                                        flex
                                        items-center
                                        gap-1
                                        text-[10.5px]
                                        font-semibold
                                        text-gray-500
                                    "
                                >

                                    {showCaprionSchemes
                                        ? "View less"
                                        : "View more"}

                                    {showCaprionSchemes ? (
                                        <ChevronUp
                                            className="h-3.5 w-3.5"
                                            aria-hidden="true"
                                        />
                                    ) : (
                                        <ChevronDown
                                            className="h-3.5 w-3.5"
                                            aria-hidden="true"
                                        />
                                    )}

                                </button>

                            </div>

                            {showCaprionSchemes && (

                                <div
                                    className="
            border-t
            border-gray-100
            bg-gray-50/70
            px-3
            py-2
        "
                                >

                                    <p className="mb-1 px-0.5 text-[10px] font-bold text-gray-800">
                                        Greshma Finvest PVT. LTD
                                    </p>

                                    <div
                                        className="
                overflow-hidden
                rounded-lg
                border
                border-gray-200
                bg-white
            "
                                    >

                                        {/* Table Header */}

                                        <div
                                            className="
                    grid
                    grid-cols-3
                    items-center
                    border-b
                    border-gray-100
                    bg-gray-50
                    px-2.5
                    py-1.5
                "
                                        >

                                            <p className="text-[8.5px] font-semibold text-gray-800">
                                                Schemes
                                            </p>

                                            <p className="text-center text-[8.5px] font-semibold text-gray-800">
                                                PF
                                            </p>

                                            <p className="text-right text-[8.5px] font-semibold text-gray-800">
                                                Interest
                                            </p>

                                        </div>


                                        {/* Schemes — scrollable so it can't push Continue off-screen */}

                                        <div
                                            className="
                    max-h-32
                    overflow-y-auto
                "
                                        >

                                            {schemes.map(
                                                (calculatedScheme) => (

                                                    <div
                                                        key={
                                                            calculatedScheme.scheme._id ??
                                                            calculatedScheme.scheme.schemeId
                                                        }
                                                        className="
                                grid
                                grid-cols-3
                                items-center
                                px-2.5
                                py-1.5
                                border-b
                                border-gray-100
                                last:border-b-0
                            "
                                                    >

                                                        <p
                                                            className="text-[9px] font-medium text-gray-800 truncate pr-1"
                                                            title={calculatedScheme.scheme.name}
                                                        >
                                                            {calculatedScheme.scheme.name}
                                                        </p>

                                                        <p className="text-center text-[9px] font-medium text-gray-700">
                                                            {calculatedScheme.scheme.processingRate}%
                                                        </p>

                                                        <p className="text-right text-[9px] font-semibold text-gray-700">
                                                            {calculatedScheme.scheme.annualInterest}%
                                                        </p>

                                                    </div>

                                                )
                                            )}

                                        </div>

                                    </div>

                                </div>

                            )}

                        </div>

                    </div>

                </div>


                {/* Continue */}

                <button
                    type="button"
                    onClick={
                        handleContinue
                    }
                    disabled={
                        isContinuing
                    }
                    className={`
                        w-full
                        py-3
                        rounded-full
                        text-sm
                        font-semibold
                        flex
                        items-center
                        justify-center
                        gap-2
                        transition-all
                        ${isContinuing
                            ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                            : "bg-gradient-to-r from-indigo-500 to-blue-400 text-white"
                        }
                    `}
                >

                    {isContinuing ? (
                        <>
                            <Loader2
                                className="
                                    h-4
                                    w-4
                                    animate-spin
                                "
                                aria-hidden="true"
                            />

                            Loading...
                        </>
                    ) : (
                        "Continue"
                    )}

                </button>


                {errorMessage && (

                    <p
                        role="alert"
                        className="
                            mt-2
                            text-center
                            text-[11px]
                            font-medium
                            text-red-500
                        "
                    >
                        {errorMessage}
                    </p>

                )}


                <p
                    className="
                        mt-3
                        text-center
                        text-[11px]
                        font-semibold
                        tracking-wide
                        text-gray-400
                    "
                >
                    CleverPe
                </p>

            </div>
        );


    /*
     * =========================================================
     * REJECTED SCREEN
     * =========================================================
     */

    const renderRejected =
        () => (

            <div
                className="
                    relative
                    flex
                    flex-col
                    h-full
                    px-6
                    pt-6
                    pb-5
                "
            >

                <div
                    className="
                        flex
                        items-center
                        justify-between
                    "
                >

                    <div />

                    <img
                        src="/CleverPeLogos/CleverPE_Black_icon.svg"
                        alt="CleverPe"
                        className="
                            h-6
                            w-6
                            opacity-90
                        "
                    />

                </div>


                <div
                    className="
                        flex
                        flex-col
                        items-center
                        flex-1
                        text-center
                        pt-3
                    "
                >

                    <div
                        className="-mt-5 w-32 h-32"
                        aria-hidden="true"
                    >

                        <Lottie
                            animationData={
                                rejectedAnimation
                            }
                            loop
                            autoplay
                        />

                    </div>


                    <h2
                        className="
                            mt-2.5
                            text-xl
                            font-bold
                            text-gray-900
                        "
                    >
                        Application is not Approved
                    </h2>


                    <p
                        className="
                            mt-1.5
                            max-w-xs
                            text-sm
                            leading-relaxed
                            text-gray-500
                        "
                    >
                        We’re sorry, but we’re unable to offer you a credit limit at this time.
                    </p>


                    <div
                        className="
                            w-full
                            mt-7
                            text-left
                        "
                    >

                        <p
                            className="
                                mb-2
                                text-[12px]
                                font-semibold
                                text-gray-700
                            "
                        >
                            Available lenders
                        </p>


                        <div
                            className="
                                w-full
                                overflow-hidden
                                rounded-xl
                                border
                                border-gray-200
                                bg-white
                                py-1
                            "
                        >

                            <div
                                className="
                                    flex
                                    items-center
                                    justify-between
                                    px-4
                                    py-3
                                "
                            >

                                <p
                                    className="
                                        text-[13px]
                                        font-bold
                                        text-gray-900
                                    "
                                >
                                    Caprion:

                                    <span
                                        className="
                                            ml-1
                                            font-semibold
                                            text-gray-500
                                        "
                                    >
                                        Not Approved
                                    </span>
                                </p>

                            </div>

                        </div>

                    </div>

                </div>


                <button
                    type="button"
                    onClick={
                        handleBack
                    }
                    className="
                        w-full
                        py-3
                        rounded-full
                        text-sm
                        font-semibold
                        flex
                        items-center
                        justify-center
                        gap-2
                        bg-gradient-to-r
                        from-indigo-500
                        to-blue-400
                        text-white
                    "
                >
                    Back
                </button>


                <p
                    className="
                        mt-3
                        text-center
                        text-[11px]
                        font-semibold
                        tracking-wide
                        text-gray-400
                    "
                >
                    CleverPe
                </p>

            </div>
        );


    /*
     * =========================================================
     * PENDING SCREEN
     * =========================================================
     */

    const renderPending =
        () => (

            <div className="relative flex flex-col h-full px-6 pt-6 pb-5">

                <div className="flex items-center justify-between">
                    <div />
                    <img
                        src="/CleverPeLogos/CleverPE_Black_icon.svg"
                        alt="CleverPe"
                        className="h-6 w-6 opacity-90"
                    />
                </div>

                <div className="flex flex-col items-center flex-1 text-center pt-3">

                    <div className="-mt-9 w-32 h-32" aria-hidden="true">
                        <Lottie animationData={successAnimation} loop autoplay />
                    </div>

                    <h2 className="-mt-2 text-xl font-bold text-gray-900">
                        Congratulations!
                    </h2>

                    <p className="mt-2 text-sm leading-relaxed text-gray-600">
                        You are approved for a loan amount up to
                    </p>

                    {approvedAmount !== null && (
                        <p className="mt-2 text-3xl font-bold bg-gradient-to-r from-indigo-500 via-purple-500 to-blue-500 text-transparent bg-clip-text">
                            {formatAmount(approvedAmount)}
                        </p>
                    )}

                    <div className="mt-2 w-full max-w-xs flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-left">
                        <AlertCircle
                            className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-amber-600"
                            aria-hidden="true"
                        />
                        <p className="text-[11px] leading-relaxed text-amber-700">
                            This amount is lower than your requested amount.
                        </p>
                    </div>

                    {/* Available lenders — now with view more/less like approved */}
                    <div className="w-full mt-5 text-left">

                        <p className="mb-2 text-[12px] font-semibold text-gray-700">
                            Available lenders
                        </p>

                        <div className="w-full overflow-hidden rounded-xl border border-gray-200 bg-white py-1">

                            <div className="flex items-center justify-between px-4 py-3">

                                <p className="text-[13px] font-bold text-gray-900">
                                    Caprion:
                                    <span className="ml-1 text-gray-900">
                                        ₹
                                        {approvedAmount !== null
                                            ? approvedAmount.toLocaleString("en-IN")
                                            : "--"}
                                    </span>
                                </p>

                                <button
                                    type="button"
                                    onClick={() =>
                                        setShowCaprionSchemes(previous => !previous)
                                    }
                                    aria-expanded={showCaprionSchemes}
                                    className="flex items-center gap-1 text-[10.5px] font-semibold text-gray-500"
                                >
                                    {showCaprionSchemes ? "View less" : "View more"}
                                    {showCaprionSchemes ? (
                                        <ChevronUp className="h-3.5 w-3.5" aria-hidden="true" />
                                    ) : (
                                        <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
                                    )}
                                </button>

                            </div>

                            {showCaprionSchemes && (

                                <div className="border-t border-gray-100 bg-gray-50/70 px-3 py-2">

                                    <p className="mb-1 px-0.5 text-[10px] font-bold text-gray-800">
                                        Greshma Finvest PVT. LTD
                                    </p>

                                    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">

                                        <div className="grid grid-cols-3 items-center border-b border-gray-100 bg-gray-50 px-2.5 py-1.5">
                                            <p className="text-[8.5px] font-semibold text-gray-800">Schemes</p>
                                            <p className="text-center text-[8.5px] font-semibold text-gray-800">PF</p>
                                            <p className="text-right text-[8.5px] font-semibold text-gray-800">Interest</p>
                                        </div>

                                        <div className="max-h-32 overflow-y-auto">

                                            {schemes.length === 0 ? (

                                                <p className="px-2.5 py-3 text-center text-[9px] text-gray-400">
                                                    No schemes available yet.
                                                </p>

                                            ) : (

                                                schemes.map(calculatedScheme => (

                                                    <div
                                                        key={calculatedScheme.scheme._id ?? calculatedScheme.scheme.schemeId}
                                                        className="grid grid-cols-3 items-center px-2.5 py-1.5 border-b border-gray-100 last:border-b-0"
                                                    >
                                                        <p
                                                            className="text-[9px] font-medium text-gray-800 truncate pr-1"
                                                            title={calculatedScheme.scheme.name}
                                                        >
                                                            {calculatedScheme.scheme.name}
                                                        </p>
                                                        <p className="text-center text-[9px] font-medium text-gray-700">
                                                            {calculatedScheme.scheme.processingRate}%
                                                        </p>
                                                        <p className="text-right text-[9px] font-semibold text-gray-700">
                                                            {calculatedScheme.scheme.annualInterest}%
                                                        </p>
                                                    </div>
                                                ))
                                            )}

                                        </div>

                                    </div>

                                </div>
                            )}

                        </div>

                    </div>

                </div>

                {/* Continue — same as approved screen */}
                {/* Back + Continue */}
                <div className="flex items-center gap-3">

                    <button
                        type="button"
                        onClick={handleBack}
                        disabled={isContinuing}
                        className="
            flex-1
            py-3
            rounded-full
            text-sm
            font-semibold
            border
            border-gray-200
            bg-white
            text-gray-700
            disabled:cursor-not-allowed
            disabled:opacity-50
        "
                    >
                        Back
                    </button>

                    <button
                        type="button"
                        onClick={handleContinue}
                        disabled={isContinuing || schemes.length === 0}
                        className={`
            flex-1 py-3 rounded-full text-sm font-semibold
            flex items-center justify-center gap-2 transition-all
            ${isContinuing || schemes.length === 0
                                ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                                : "bg-gradient-to-r from-indigo-500 to-blue-400 text-white"
                            }
        `}
                    >
                        {isContinuing ? (
                            <>
                                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                                Loading...
                            </>
                        ) : (
                            "Continue"
                        )}
                    </button>

                </div>

                {errorMessage && (
                    <p role="alert" className="mt-2 text-center text-[11px] font-medium text-red-500">
                        {errorMessage}
                    </p>
                )}

                <p className="mt-3 text-center text-[11px] font-semibold tracking-wide text-gray-400">
                    CleverPe
                </p>

            </div>
        );


    /*
     * =========================================================
     * ERROR SCREEN
     * =========================================================
     */

    const renderError =
        () => (

            <div
                className="
                    flex
                    flex-col
                    h-full
                    px-6
                    pt-10
                    pb-5
                "
            >

                <div
                    className="
                        flex
                        flex-col
                        items-center
                        justify-center
                        flex-1
                        text-center
                    "
                >

                    <XCircle
                        className="
                            w-16
                            h-16
                            text-red-400
                        "
                        aria-hidden="true"
                    />


                    <h2
                        className="
                            mt-5
                            text-lg
                            font-bold
                            text-gray-900
                        "
                    >
                        Something went wrong
                    </h2>


                    <p
                        className="
                            mt-2
                            text-sm
                            text-gray-500
                        "
                    >
                        {errorMessage ||
                            "Unable to fetch your pre-approval status."}
                    </p>

                </div>


                <button
                    type="button"
                    onClick={() =>
                        window.location.reload()
                    }
                    className="
                        w-full
                        py-3
                        rounded-full
                        text-sm
                        font-semibold
                        bg-gradient-to-r
                        from-indigo-500
                        to-blue-400
                        text-white
                    "
                >
                    Try again
                </button>


                <p
                    className="
                        mt-3
                        text-center
                        text-[11px]
                        font-semibold
                        tracking-wide
                        text-gray-400
                    "
                >
                    CleverPe
                </p>

            </div>
        );


    /*
     * =========================================================
     * RENDER
     * =========================================================
     */

    return (

        <MobileFrame
            glowColor="#c7d2fe"
            borderColor="#e5e7eb"
            width={420}
        >

            <div
                className="
                    relative
                    w-full
                    h-full
                    overflow-hidden
                    bg-gradient-to-br
                    from-white
                    via-gray-50
                    to-gray-100
                "
            >

                {/* Decorative background */}

                <div
                    aria-hidden="true"
                    className="
                        absolute
                        w-64
                        h-64
                        bg-purple-200
                        rounded-full
                        -top-24
                        -left-20
                        blur-3xl
                        opacity-20
                    "
                />


                <div
                    aria-hidden="true"
                    className="
                        absolute
                        w-64
                        h-64
                        bg-blue-200
                        rounded-full
                        -bottom-24
                        -right-20
                        blur-3xl
                        opacity-20
                    "
                />


                <div
                    className="
                        relative
                        z-10
                        w-full
                        h-full
                    "
                >

                    {pageState === "loading" &&
                        renderLoading()}

                    {pageState === "approved" &&
                        renderApproved()}

                    {pageState === "rejected" &&
                        renderRejected()}

                    {pageState === "pending" &&
                        renderPending()}

                    {pageState === "error" &&
                        renderError()}

                </div>

            </div>

        </MobileFrame>
    );
};


export default PreApproval;